import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { AntdModal, Card } from '@brokerbay/design-components';
import { Modal, Spin, Button } from 'components';

import { CardInfo } from 'api/BillingController';
import { Color } from '@bb/elements-web';
import { featureEnabled } from 'utils';
import { BillingInvoicesPageContext } from '../../pages/BillingInvoicesPage/BillingInvoicesPageContext';
import { ManageCardContext } from './ManageCardContext';
import {
  PoweredByStripeLogo,
  renderCreditCardIcon,
  renderCreditCardName,
} from './CreditCardIcons';

export const SHOW_UPDATE_CARD_EVENT = (userId: string) =>
  `onBillingPageShowUpdateCard_${userId}`;

const Content = () => {
  const {
    customerId,
    cardInfo,
    loadingCardInfo,
    setModalVisible,
    canManageBilling,
    isSubscriptionOverdue,
    createCustomer,
    loadingCreateCustomerId,
    isBillingPage,
    userId,
    showJPMC,
  } = useContext(ManageCardContext);
  const { loadingOverdueFees } = useContext(BillingInvoicesPageContext);

  const noPaymentInfo = !customerId || !cardInfo || !cardInfo.type;
  // Define the information for the modals
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const overdueBalanceResponse = isSubscriptionOverdue.response;
  const overdueBalanceMessage =
    'message' in isSubscriptionOverdue ? isSubscriptionOverdue.message : null;
  const manageBillingResponse = canManageBilling.response;
  const manageBillingMessage =
    'message' in canManageBilling ? canManageBilling.message : undefined;
  // A ref that stores either null, or the function for after agreeing to pay the overdue amount.
  const overdueConfirmationAction = useRef<(() => void) | null>(null);

  // The function to check if the user is eligible to perform their action,
  // and if so, allows the action to proceed.
  const handleCardAction = useCallback(
    async (action: () => void) => {
      // First check if they are assuming a user
      if (manageBillingResponse) {
        // Then check if they have an overdue balance
        if (overdueBalanceResponse) {
          setShowOverdueModal(true);
          overdueConfirmationAction.current = action;
        } else {
          await action();
        }
      } else {
        Modal.error({
          title: 'You are not authorized to manage cards',
          content: manageBillingMessage,
          centered: true,
        });
      }
    },
    [manageBillingMessage, manageBillingResponse, overdueBalanceResponse],
  );

  // What to do for updating a card
  const handleUpdateCardClick = useCallback(() => {
    handleCardAction(() => setModalVisible(true));
  }, [handleCardAction, setModalVisible]);

  useEffect(() => {
    document.addEventListener(
      SHOW_UPDATE_CARD_EVENT(userId),
      handleUpdateCardClick,
    );
    return () => {
      document.removeEventListener(
        SHOW_UPDATE_CARD_EVENT(userId),
        handleUpdateCardClick,
      );
    };
  }, [handleUpdateCardClick, userId]);

  // What to do for adding a card
  const handleAddCardClick = () =>
    handleCardAction(async () => {
      if (!customerId) {
        await createCustomer();
      } else {
        setModalVisible(true);
      }
    });

  const renderCardInfo = (targetCardInfo?: CardInfo) => {
    if (loadingCardInfo) {
      return <Spin center />;
    }
    if (!targetCardInfo) {
      if (isBillingPage && !userId) {
        // if we allow org level billing to create stripe customer id on Add Card button press,
        // revisit logic in automaton to ensure stripe customer data is saved in correct
        // repository (StripeCustomer vs StripeClientInfo)
        return (
          <div data-cy="container-no-card">
            No default card setup for account. Please contact our customer
            success team.
          </div>
        );
      }

      return (
        <>
          <Button onClick={handleAddCardClick} data-cy="add-card-button">
            {loadingCreateCustomerId ? <Spin center /> : 'Add Card'}
          </Button>
          {!showJPMC && <PoweredByStripeLogo />}
        </>
      );
    }

    return (
      <>
        <StyledCardInfoRow>
          <CardInfoText>
            {renderCreditCardIcon(targetCardInfo.type)}
            <span data-cy="stripe-card-type">
              {renderCreditCardName(targetCardInfo.type)} Credit Card
            </span>
            <span data-cy="stripe-card-digits">
              **** **** **** {targetCardInfo.lastDigits}
            </span>
          </CardInfoText>
          <Button onClick={handleUpdateCardClick} data-cy="change-card-button">
            Update
          </Button>
        </StyledCardInfoRow>

        {!showJPMC && <PoweredByStripeLogo />}
      </>
    );
  };

  return (
    <StyledContainer>
      {/* The modal we should show if they are overdue in their subscription payment. */}
      <AntdModal
        open={showOverdueModal}
        title={'Payment Details'}
        centered
        okText={'Pay Now'}
        okButtonProps={{
          style: { marginLeft: '0px' },
          autoFocus: true,
          disabled: loadingOverdueFees !== 'done',
          block: true,
        }}
        onOk={() => {
          setShowOverdueModal(false);
          // Run the saved action
          overdueConfirmationAction.current?.();
        }}
        onCancel={() => setShowOverdueModal(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {overdueBalanceMessage}
      </AntdModal>

      {/* The actual "card" we show on the page displaying basic details */}
      {!featureEnabled('subscriptionHubFeature') && (
        <Card title={isBillingPage ? 'Manage Cards' : ''}>
          <StyledCardBody center={!isBillingPage} noPaymentInfo={noPaymentInfo}>
            {renderCardInfo(cardInfo)}
          </StyledCardBody>
        </Card>
      )}
      {featureEnabled('subscriptionHubFeature') && renderCardInfo(cardInfo)}
    </StyledContainer>
  );
};

const StyledCardInfoRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  gap: 8px;
`;

const CardInfoText = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledCardBody = styled.div<{
  center?: boolean;
  noPaymentInfo?: boolean;
}>`
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  padding: 16px;

  display: flex;
  flex-direction: column;
  align-items: ${props => (props.center ? 'center' : 'flex-start')};
  gap: ${props => (props.noPaymentInfo ? '0' : '12px')};
  svg#powered-by-stripe {
    grid-column: 1 / 3;
    margin-top: 0;
  }
`;

const StyledContainer = styled.div`
  svg#powered-by-stripe {
    width: 100px;
    margin-top: 10px;
  }
`;

export default Content;
